/**
 * TrendChart - admin の日次推移ミニ棒グラフ
 *
 * - 縦軸に数値スケール (最大値 / 中間 / 0)
 * - バーにカーソルを合わせると日付と数値をツールチップ表示
 *
 * data: [{ date: 'YYYY-MM-DD', count: number }, ...]
 */
import React, { useState } from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const YAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${(p) => p.$h}px;
  width: 32px;
  flex-shrink: 0;
  text-align: right;
  font-size: 0.66rem;
  font-family: ${(p) => p.theme.fonts?.mono};
  color: ${(p) => p.theme.colors.textMuted};
`;

const ChartArea = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: ${(p) => p.$h}px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

/* バー全列をホバー領域にするためのスロット */
const Slot = styled.div`
  flex: 1;
  min-width: 3px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  cursor: pointer;
`;

const Bar = styled.div`
  width: 100%;
  background: ${(p) => p.$accent || p.theme.colors.primary};
  opacity: ${(p) => (p.$zero ? 0.12 : p.$active ? 1 : 0.8)};
  border-radius: 2px 2px 0 0;
  height: ${(p) => Math.max(p.$pct, 2)}%;
  transition: opacity 0.12s;
`;

const Tip = styled.div`
  position: absolute;
  top: -6px;
  left: ${(p) => p.$left}%;
  transform: translateX(-50%);
  background: ${(p) => p.theme.colors.bgHover};
  border: 1px solid ${(p) => p.theme.colors.borderHover};
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 0.7rem;
  font-family: ${(p) => p.theme.fonts?.mono};
  color: ${(p) => p.theme.colors.text};
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
`;

const XAxis = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.66rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 5px;
`;

function TrendChart({ data, height = 90, accent }) {
  const [hover, setHover] = useState(null);

  if (!data || data.length === 0) return null;

  const max = Math.max(1, ...data.map((d) => d.count));
  const n = data.length;
  // ツールチップが端で切れないよう left を 6〜94% にクランプ
  const tipLeft = hover != null
    ? Math.min(94, Math.max(6, ((hover + 0.5) / n) * 100))
    : 0;

  return (
    <Wrap>
      <Row>
        <YAxis $h={height}>
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </YAxis>
        <ChartArea>
          <Bars $h={height}>
            {data.map((d, i) => (
              <Slot
                key={d.date}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <Bar
                  $pct={(d.count / max) * 100}
                  $zero={d.count === 0}
                  $active={hover === i}
                  $accent={accent}
                />
              </Slot>
            ))}
          </Bars>
          {hover != null && (
            <Tip $left={tipLeft}>
              {data[hover].date} : {data[hover].count}
            </Tip>
          )}
        </ChartArea>
      </Row>
      <Row>
        <div style={{ width: 32, flexShrink: 0 }} />
        <XAxis style={{ flex: 1 }}>
          <span>{data[0].date.slice(5)}</span>
          <span>{data[n - 1].date.slice(5)}</span>
        </XAxis>
      </Row>
    </Wrap>
  );
}

export default TrendChart;
